/**
 * Gestione consenso cookie (GDPR / nLPD).
 *
 * Architettura:
 * - `public/index.html` imposta Consent Mode v2 in default-deny PRIMA che gtag.js si carichi.
 * - Questo modulo legge/scrive la scelta utente in `localStorage`
 *   (chiave `ticino-wild-cookie-consent`) e chiama `gtag('consent','update', ...)`.
 * - `CookieBanner.jsx` decide se mostrarsi in base a `hasConsentChoice()`.
 * - `CookieSettings.jsx` consente di revocare/modificare la scelta in qualunque momento.
 *
 * Categorie:
 * - `necessary`: tecnici, sempre attivi (JWT, lingua, carrello, pending checkout). Non opzionabili.
 * - `analytics`: Google Analytics 4 (gtag.js). Opzionale.
 *
 * Per ora le uniche categorie reali sono queste due. Newsletter/marketing/ads non sono usati.
 */

export const CONSENT_KEY = 'ticino-wild-cookie-consent';
export const CONSENT_VERSION = 1;

/**
 * Legge la scelta utente da localStorage. Restituisce `null` se non c'è ancora
 * una scelta (o se la versione salvata è obsoleta e va re-prompted).
 */
export const getConsent = () => {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Versione diversa → invalida la scelta vecchia (forza re-prompt se aggiungiamo categorie)
        if (parsed.version !== CONSENT_VERSION) return null;
        return parsed;
    } catch {
        return null;
    }
};

export const hasConsentChoice = () => getConsent() !== null;

/**
 * Salva la scelta utente e propaga lo stato a gtag tramite Consent Mode v2.
 * `analytics` è il solo flag opzionale al momento; necessary è sempre true.
 */
export const setConsent = ({ analytics }) => {
    const payload = {
        version: CONSENT_VERSION,
        necessary: true,
        analytics: Boolean(analytics),
        timestamp: new Date().toISOString(),
    };
    try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
    } catch {
        // localStorage può fallire in modalità privata / quota piena: il consenso resta in memoria per la sessione
    }
    applyConsentToGtag(payload);
    return payload;
};

/**
 * Cancella la scelta salvata (utile per testing; non esposto in UI).
 */
export const resetConsent = () => {
    try {
        localStorage.removeItem(CONSENT_KEY);
    } catch {
        /* noop */
    }
};

/**
 * Invia lo stato consenso a gtag tramite Consent Mode v2.
 * Sicuro da chiamare anche se gtag non è (ancora) caricato: i comandi finiscono
 * in `window.dataLayer` e vengono processati quando gtag.js è pronto.
 */
export const applyConsentToGtag = (consent) => {
    if (typeof window === 'undefined') return;
    // Definizione difensiva: dovrebbe già esistere da index.html, ma in build SSG potrebbe mancare
    window.dataLayer = window.dataLayer || [];
    const gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    gtag('consent', 'update', {
        analytics_storage: consent.analytics ? 'granted' : 'denied',
        // Le altre categorie restano 'denied' come da default (non usiamo ads/personalizzazione)
    });
};

/**
 * Applica all'avvio dell'app la scelta già salvata (se presente).
 * Da chiamare una volta in `App.js` boot — se l'utente non ha ancora scelto,
 * Consent Mode resta in default-deny e il banner verrà mostrato.
 */
export const initConsentOnBoot = () => {
    const existing = getConsent();
    if (existing) applyConsentToGtag(existing);
};
